import React, { useCallback, useEffect, useState } from "react";
import {
  Card, CardBody, CardHeader, Col, Container, Row, Table, Badge, Alert, Spinner,
  Button, Nav, NavItem, NavLink, Input,
} from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { listAccessRequests, updateAccessRequest } from "../../services/accessRequestService";

/**
 * The early-access queue.
 *
 * Marking a request "granted" records the decision — it does not change anyone's
 * tier. Upgrading a member stays a deliberate act against the subscriptions
 * table, so nobody is given paid access by a mis-click here.
 */

const fmtDate = (v) => (v ? new Date(v).toLocaleString() : "—");

const STATUS_COLOR = {
  pending: "warning",
  contacted: "info",
  granted: "success",
  declined: "secondary",
};

const FILTERS = [
  { id: "", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "contacted", label: "Contacted" },
  { id: "granted", label: "Granted" },
  { id: "declined", label: "Declined" },
];

const AccessRequests = () => {
  document.title = "Access Requests | ForecastNest";

  const [filter, setFilter] = useState("pending");
  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [notes, setNotes] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listAccessRequests(filter ? { status: filter } : {});
      setRequests(res.data?.requests || []);
      setSummary(res.data?.summary || null);
    } catch (e) {
      setFeedback({ type: "danger", text: "Could not load the queue." });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const move = async (row, status) => {
    setBusyId(row.id);
    setFeedback(null);
    try {
      await updateAccessRequest(row.id, { status, adminNote: notes[row.id] || null });
      setFeedback({ type: "success", text: `${row.email} → ${status}` });
      await load();
    } catch (e) {
      setFeedback({ type: "danger", text: e?.error || "Could not update that request." });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Access Requests" pageTitle="System Admin" />

        {feedback && (
          <Alert color={feedback.type} toggle={() => setFeedback(null)}>{feedback.text}</Alert>
        )}

        <Card>
          <CardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <Nav pills className="mb-0">
              {FILTERS.map((f) => (
                <NavItem key={f.id || "all"}>
                  <NavLink href="#" className={filter === f.id ? "active" : ""}
                           onClick={(e) => { e.preventDefault(); setFilter(f.id); }}>
                    {f.label}
                    {summary && f.id && summary[f.id] > 0 && (
                      <Badge color="light" className="text-body ms-1">{summary[f.id]}</Badge>
                    )}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
            {summary && (
              <span className="text-muted fs-13">{summary.total} total</span>
            )}
          </CardHeader>

          <CardBody>
            {loading ? (
              <div className="text-center py-4"><Spinner /></div>
            ) : requests.length === 0 ? (
              <p className="text-muted text-center py-4 mb-0">
                Nothing here{filter ? ` with status "${filter}"` : ""}.
              </p>
            ) : (
              <div className="table-responsive">
                <Table className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Member</th>
                      <th>Wants</th>
                      <th>Research</th>
                      <th>What they said</th>
                      <th>Asked</th>
                      <th>Status</th>
                      <th style={{ minWidth: 260 }}>Move it along</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <div className="fw-medium">{r.name}</div>
                          <a href={`mailto:${r.email}`} className="text-muted fs-12">{r.email}</a>
                        </td>
                        <td>
                          {r.requested_tier
                            ? <Badge color="primary">Tier {r.requested_tier}</Badge>
                            : <span className="text-muted fs-13">Unspecified</span>}
                        </td>
                        <td>
                          {/* How much they have actually recorded. A request from
                              someone with forty forecasts is a different request
                              from one who signed up an hour ago. */}
                          <span className={r.research_count > 0 ? "fw-medium" : "text-muted"}>
                            {r.research_count}
                          </span>
                        </td>
                        <td style={{ maxWidth: 320 }}>
                          <div className="fs-13 text-muted">{r.message || "—"}</div>
                          {r.admin_note && (
                            <div className="fs-12 mt-1"><em>Note: {r.admin_note}</em></div>
                          )}
                        </td>
                        <td className="fs-13 text-muted">{fmtDate(r.created_at)}</td>
                        <td><Badge color={STATUS_COLOR[r.status]}>{r.status}</Badge></td>
                        <td>
                          <Input bsSize="sm" className="mb-2" placeholder="Note (optional)"
                                 value={notes[r.id] ?? (r.admin_note || "")}
                                 onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })} />
                          <div className="d-flex flex-wrap gap-1">
                            {["contacted", "granted", "declined"].map((s) => (
                              <Button key={s} size="sm" color="light"
                                      disabled={busyId === r.id || r.status === s}
                                      onClick={() => move(r, s)}>
                                {busyId === r.id ? <Spinner size="sm" /> : s}
                              </Button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>

        <Row>
          <Col>
            <p className="text-muted fs-12">
              Marking a request granted records the decision. It does not change the
              member's tier — do that against their subscription, so nobody is upgraded
              by a mis-click here.
            </p>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AccessRequests;
